import { useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Encounter } from "@medplum/fhirtypes";

import { Button, Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { ProfileContext } from "@/contexts/profile";
import SignAndLockConfirmModal from "@/encounters/components/SignAndLockConfirmModal";
import { getAmendmentPath } from "@/encounters/utils/amendment-paths";
import { signAndLockAmendment } from "@/encounters/utils/amendment-sign-and-lock";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

import { EncounterSignature } from "../../EncounterSignature/EncounterSignature";

interface EncounterFooterProps {
  encounter: Encounter;
  /** Signs the encounter. Resolves `true` only when the encounter was signed. */
  onSignAndLock?: () => Promise<boolean>;
  form?: UseFormReturnType<Record<string, unknown>>;
  isSubmitting?: boolean;
}

export const EncounterFooter = ({
  encounter,
  onSignAndLock,
  form,
  isSubmitting = false,
}: EncounterFooterProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPatient } = useContext(ProfileContext);
  const [opened, handlers] = useDisclosure(false);
  const isAmendment = useMemo(() => location.pathname.endsWith("amend"), [location.pathname]);

  const handleSignAndLockClick = () => {
    if (onSignAndLock) {
      // If we have a form in amendment mode, validate and submit directly (no modal)
      if (form && isAmendment) {
        signAndLockAmendment(form, onSignAndLock);
      } else {
        handlers.open();
      }
    }
  };

  const handleConfirmSignAndLock = async () => {
    if (onSignAndLock) {
      // onSignAndLock will handle its own validation/parsing, just call it directly
      onSignAndLock();
      handlers.close();
    }
  };

  return (
    <>
      <Stack gap="xs" align="flex-end">
        {!isPatient && (
          <>
            {isAmendment && onSignAndLock ? (
              <PermissionGate mode="disable" requiredPermissions={PROVIDER_PERMISSION_FLAGS}>
                <Button
                  size="sm"
                  disabled={isSubmitting}
                  onClick={handleSignAndLockClick}
                  data-testid="encounter-footer-sign-lock-button"
                >
                  Sign & Lock
                </Button>
              </PermissionGate>
            ) : (
              <Button
                size="sm"
                c="red.7"
                variant="outline"
                bd="1px solid red.7"
                data-testid="encounter-footer-amend-button"
                onClick={() =>
                  navigate(getAmendmentPath(encounter), {
                    preventScrollReset: true,
                  })
                }
              >
                Amend Encounter
              </Button>
            )}
          </>
        )}
        <EncounterSignature encounter={encounter} />
      </Stack>
      {!isAmendment && onSignAndLock && (
        <SignAndLockConfirmModal
          opened={opened}
          onClose={handlers.close}
          onConfirm={handleConfirmSignAndLock}
        />
      )}
    </>
  );
};
